import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Col, Form, Checkbox, Flex, FormInstance, message, Spin } from 'antd';
import { Link } from 'react-router-dom';
import {
  CustomSelect,
  AddSelectableItem,
  CustomShipmentText,
} from '../../../../components';
import ShipmentAlertThresholds from './ShipmentAlertThresholds';
import {
  AlertType,
  ETAChangedByList,
  GxPReportEventOptions,
} from '../../../../common/static_data';
import { DownOutlined } from '@ant-design/icons';
import { GetLocationsWithPaginationAndSearch } from '@/apis/onboarding';
import { useToken } from '@/common/helper';
import { useUnAuthorize } from '@/hooks';
import SearchInput from '@/components/SearchInput';
import { InventoryLocationData, LocationData, TeamData } from '@/apis/types';
import { enumToArrayWithSpaces } from '@/common/utils/utils';
import { InboundNotification } from '@/common/enum';
import { subItemsData, GetInventoryListV2 } from '@/apis/inventory';
import { InventoryItemRow } from '@/pages/Inventory/type';

interface Shipment {
  id?: string;
  sensor?: unknown;
}

export interface AlertDetail {
  alert_type: string;
  alert_name: string;
  threshold_value: string;
  threshold_comparison?: string;
  unit: string;
  sensor_required?: string;
  [key: string]: unknown;
}

export interface AlertThreshold {
  alerts: AlertDetail[];
  id?: string;
  name?: string;
  phase_display: string;
}

export type SelectedThresholdValues = Record<string, string | number>;

interface AlertFieldByTypeProps {
  form: FormInstance;
  alertType: AlertType;
  step: number;
  shipment: Shipment;
  setStep: (step: number) => void;
  selectAllAlertTypes: boolean;
  setSelectAllAlertTypes: (value: boolean) => void;
  alertThreshold: AlertThreshold[];
  setSelectedThresholdValues: (
    value:
      | SelectedThresholdValues
      | ((prev: SelectedThresholdValues) => SelectedThresholdValues)
  ) => void;
  selectedThresholdValues: SelectedThresholdValues;
  maxHeight?: string;
  showThresholdInputSection?: boolean;
  enableAllThresholds?: boolean;
  shipmentTeams?: TeamData[];
  setSelectedTeams?: React.Dispatch<React.SetStateAction<string | ''>>;
  isInventory?: boolean;
  selecteInventory?: InventoryItemRow;
  isLoading?: boolean;
}

/**
 * Renders different form fields based on the alert notification type
 *
 * @param form - Ant Design form instance for validation and field access
 * @param alertType - Type of alert determining which fields to render
 * @param step - Current step in the notification creation flow
 * @param shipment - Shipment data including sensor information
 * @param setStep - Callback to change the current step
 * @param selectAllAlertTypes - Whether all alert types are selected
 * @param setSelectAllAlertTypes - Callback to toggle select all state
 * @param alertThreshold - Array of available alert threshold configurations
 * @param setSelectedThresholdValues - Callback to update selected threshold values
 * @param selectedThresholdValues - Currently selected threshold values
 * @param maxHeight - Maximum height for scrollable threshold list
 * @param showThresholdInputSection - Whether to show threshold input section
 * @param enableAllThresholds - Whether to enable all thresholds regardless of sensor
 */
const AlertFieldByType: React.FC<AlertFieldByTypeProps> = ({
  form,
  alertType,
  step,
  shipment,
  setStep,
  selectAllAlertTypes,
  setSelectAllAlertTypes,
  alertThreshold,
  setSelectedThresholdValues,
  selectedThresholdValues,
  maxHeight,
  showThresholdInputSection,
  enableAllThresholds = false,
  setSelectedTeams,
  shipmentTeams,
  isInventory,
  selecteInventory,
  isLoading,
}) => {
  const { token, decodedUserToken } = useToken();
  const isWorldCourierCompany = Boolean(decodedUserToken()?.enablecustomerleg);
  const { unauthorize } = useUnAuthorize();
  const [locationsList, setLocationsList] = useState<LocationData[]>([]);
  const [inventoryLocationsList, setInventoryLocationsList] = useState<
    InventoryLocationData[]
  >([]);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>('');
  const [inventorySearch, setInventorySearch] = useState<string>('');
  const [dropdownLoading, setDropdownLoading] = useState<boolean>(false);
  const dropdownScrollElRef = useRef<HTMLDivElement | null>(null);
  const [selectedItem, setSelectedItem] = useState<subItemsData | null>(null);
  const actionTypeList = enumToArrayWithSpaces(InboundNotification).map(
    (item) => ({
      ...item,
      // disabled: item.value !== 1,
    })
  );
  const oneTimeAction = actionTypeList.find(
    (x) => x.label === 'One time message sent'
  );
  if (oneTimeAction) {
    oneTimeAction.label = 'One-time message sent (email/call)';
  }

  const handleTeamsChange = (val: string) => {
    setSelectedTeams(val);
  };

  const handleItemChange = (value: string) => {
    const item = subItemsList.find((item) => item.id === value);
    setSelectedItem(item);
    if (item && item.locations) {
      const locations = item.locations.map((loc) => ({
        id: loc.location_id,
        name: loc.name,
      }));
      setInventoryLocationsList(locations);
    } else {
      setInventoryLocationsList([]);
    }
    form.setFieldValue('locations', []);
    form.setFieldValue('item', value ? [value] : []);
  };

  useEffect(() => {
    if (shipmentTeams?.length === 1) {
      setSelectedTeams(shipmentTeams[0].id);
    }
  }, [shipmentTeams, form, setSelectedTeams]);

  const fetchLocations = useCallback(
    (checked: boolean, pageNum: number, q: string) => {
      setDropdownLoading(true);
      // if (useGlobalSpin && !q) showSpin();
      GetLocationsWithPaginationAndSearch(token, checked, pageNum, q)
        .then((res) => {
          const data = res?.data?.data;
          if (res?.data?.data?.is_success) {
            setLocationsList(data?.data?.data || []);
            setTotalPages(data?.page_count);
            setPage(pageNum);
          } else {
            message.error(data?.message || 'Failed to fetch locations');
          }
        })
        .catch((error) => {
          unauthorize(error);
          message.error('Failed to fetch locations');
        })
        .finally(() => {
          // if (useGlobalSpin) hideSpin();
          setDropdownLoading(false);
        });
    },
    [token, unauthorize]
  );

  const fetchMore = useCallback(() => {
    if (dropdownLoading || page >= totalPages) return;
    const nextPage = page + 1;
    setDropdownLoading(true);
    GetLocationsWithPaginationAndSearch(token, false, nextPage, search)
      .then((res) => {
        const data = res?.data?.data;
        if (data?.is_success) {
          setLocationsList((prev) => [...prev, ...(data?.data?.data || [])]);
          setPage(nextPage);
        }
      })
      .catch((error) => {
        unauthorize(error);
        message.error('Failed to load more locations');
      })
      .finally(() => {
        setDropdownLoading(false);
        const el = dropdownScrollElRef.current;
        if (el) {
          requestAnimationFrame(() => {
            el.scrollTop = el.scrollHeight;
          });
        }
      });
  }, [dropdownLoading, page, totalPages, token, search, unauthorize]);

  const firstRunRef = useRef(true);

  useEffect(() => {
    if (isInventory) {
      setInventoryLocationsList(
        selecteInventory?.locations?.map((loc) => ({
          id: loc?.location_id,
          name: loc?.name,
        })) || []
      );
    } else {
      if (firstRunRef.current) {
        firstRunRef.current = false;
        fetchLocations(false, 1, search);
        return;
      }
      const t = setTimeout(() => {
        fetchLocations(false, 1, search);
      }, 400);
      return () => clearTimeout(t);
    }
  }, [search, fetchLocations, isInventory, selecteInventory?.locations]);

  const handlePopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      if (!target) return;
      dropdownScrollElRef.current = target;
      const nearBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - 24;
      if (nearBottom) fetchMore();
    },
    [fetchMore]
  );

  // subitems fetch START

  //   sub-items
  const PAGE_SIZE = 50;
  const [subItemsList, setSubItemsList] = useState<subItemsData[]>([]);
  const [subItemTotalPages, setSubItemTotalPages] = useState<number>(1);
  const [subItemPage, setSubItemPage] = useState<number>(1);
  const [subItemSearch, setSubItemSearch] = useState<string>('');
  const [subItemdropdownLoading, setSubItemDropdownLoading] =
    useState<boolean>(false);
  const subItemDropdownScrollElRef = useRef<HTMLDivElement>(null);

  const loadSubItems = useCallback(
    (pageNum: number, q: string, append = false) => {
      setSubItemDropdownLoading(true);
      const model = {
        search: q,
        page: pageNum,
        page_size: PAGE_SIZE,
      };
      GetInventoryListV2(token, model)
        .then((res) => {
          const data = res?.data?.data;
          if (data?.is_success) {
            const incoming = data?.data?.results || [];
            setSubItemsList((prev) =>
              append ? [...prev, ...incoming] : incoming
            );
            setSubItemTotalPages(data?.data?.page_count);
            setSubItemPage(pageNum);
          } else {
            message.error(data?.message || 'Failed to fetch sub-items');
          }
        })
        .catch((error) => {
          unauthorize(error);
          message.error('Failed to fetch sub-items');
        })
        .finally(() => setSubItemDropdownLoading(false));
    },
    [token, unauthorize, PAGE_SIZE]
  );

  const fetchSubItemMore = useCallback(() => {
    if (subItemdropdownLoading || subItemPage >= subItemTotalPages) return;
    const nextPage = subItemPage + 1;
    loadSubItems(nextPage, subItemSearch, true);
  }, [
    subItemdropdownLoading,
    subItemPage,
    subItemTotalPages,
    loadSubItems,
    subItemSearch,
  ]);

  useEffect(() => {
    loadSubItems(1, subItemSearch);
  }, [subItemSearch, loadSubItems]);

  const handleSubItemPopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      if (!target) return;
      subItemDropdownScrollElRef.current = target;
      const nearBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - 50;
      if (
        nearBottom &&
        !subItemdropdownLoading &&
        subItemPage < subItemTotalPages
      ) {
        fetchSubItemMore();
      }
    },
    [fetchSubItemMore, subItemdropdownLoading, subItemPage, subItemTotalPages]
  );
  // subitems fetch END

  useEffect(() => {
    if (selecteInventory?.id) {
      form.setFieldsValue({
        item: [selecteInventory?.id],
        locations:
          selecteInventory?.locations?.map((loc) =>
            typeof loc === 'string' ? loc : loc?.location_id || loc?.id
          ) || [],
      });
    }
  }, [selecteInventory, form]);

  // Populate locations based on selected item for edit mode
  useEffect(() => {
    const timer = setTimeout(() => {
      const itemValue = form.getFieldValue('item');
      if (
        itemValue &&
        Array.isArray(itemValue) &&
        itemValue.length > 0 &&
        subItemsList.length > 0
      ) {
        const itemId =
          typeof itemValue[0] === 'object' ? itemValue[0].value : itemValue[0];

        const item = subItemsList.find((item) => item.id === itemId);

        if (item && item.locations) {
          const locations = item.locations.map((loc) => ({
            id: loc.location_id,
            name: loc.name,
          }));
          setInventoryLocationsList(locations);
        } else {
          setInventoryLocationsList([]);
        }
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [form, subItemsList]);

  // Reset locations when selectedItem changes
  useEffect(() => {
    if (selectedItem) {
      form.setFieldValue('locations', []);
    }
  }, [selectedItem, form]);

  // Handle initial form values for edit mode

  const result = [
    {
      value: selecteInventory?.id,
      label: selecteInventory?.name,
    },
  ];
  const gxpReportEvents: string[] = Form.useWatch('gxp_report', form) ?? [];

  switch (alertType) {
    case 'CRITICAL_ALERT':
      return (
        <Form.Item>
          <>
            {step !== 3 && (
              <>
                {shipmentTeams?.length > 1 && (
                  <Flex vertical style={{ marginBottom: '16px' }}>
                    <CustomShipmentText
                      text={'Teams'}
                      fontSize='14px'
                      fontWeight={600}
                      marginBottom={'4px'}
                    />
                    <Form.Item
                      name='team_id'
                      rules={[
                        { required: true, message: 'Please select a team' },
                      ]}
                      style={{ marginBottom: '10px' }}
                    >
                      <CustomSelect
                        width='100%'
                        placeholder='Select...'
                        suffixIcon={
                          <DownOutlined
                            style={{ color: 'var(--text-secondary)' }}
                          />
                        }
                        options={shipmentTeams?.map((team) => ({
                          value: team.id,
                          label: team.name,
                        }))}
                        bgColor='var(--light-gray)'
                        textColor='var(--text-secondary)'
                        borderRadius='8px'
                        height='36px'
                        border='none'
                        placeholderColor='var(--text-secondary)'
                        filterOption={false}
                        onChange={(value) => handleTeamsChange(value)}
                      />
                    </Form.Item>
                  </Flex>
                )}
                <div>
                  <CustomShipmentText
                    text={'Alert type'}
                    fontSize='14px'
                    fontWeight={600}
                    marginBottom='4px'
                  />
                  <CustomShipmentText
                    color='var(--text-secondary)'
                    marginBottom='8px'
                    text={
                      'Choose trigger alerts and modify their default thresholds'
                    }
                  />
                </div>
              </>
            )}
          </>
          <ShipmentAlertThresholds
            shipment={shipment}
            selectAllAlertTypes={selectAllAlertTypes}
            setSelectAllAlertTypes={setSelectAllAlertTypes}
            step={step}
            alertThreshold={alertThreshold}
            setSelectedThresholdValues={setSelectedThresholdValues}
            selectedThresholdValues={selectedThresholdValues}
            maxHeight={maxHeight}
            showThresholdInputSection={showThresholdInputSection}
            enableAllThresholds={enableAllThresholds}
            isLoading={isLoading}
          />
          {step !== 3 && (
            <AddSelectableItem text='Select alert' onClick={() => setStep(3)} />
          )}
        </Form.Item>
      );

    case 'PICKUP_DELIVERY':
      return (
        <div>
          <CustomShipmentText
            text={'Shipment status change'}
            fontSize='14px'
            fontWeight={600}
            marginBottom='10px'
          />
          <Form.Item
            shouldUpdate
            name='confirmation'
            dependencies={[
              'is_created',
              'is_pickup_confirmation',
              'is_delivery_confirmation',
              'is_delivery_exception',
              'is_incomplete',
            ]}
            rules={[
              {
                validator: () => {
                  const pickup = form.getFieldValue('is_pickup_confirmation');
                  const delivery = form.getFieldValue(
                    'is_delivery_confirmation'
                  );
                  const exception = form.getFieldValue('is_delivery_exception');
                  const created = form.getFieldValue('is_created');
                  const incomplete = form.getFieldValue('is_incomplete');
                  if (pickup || delivery || exception || created || incomplete)
                    return Promise.resolve();
                  return Promise.reject(
                    new Error('Shipment status change cannot be empty')
                  );
                },
              },
            ]}
          >
            <Flex vertical gap={8}>
              <Form.Item name='is_created' valuePropName='checked' noStyle>
                <Checkbox className='notification-checkbox'>
                  <span className='notification-checkbox-label'>Created</span>
                </Checkbox>
              </Form.Item>
              <Form.Item
                name='is_pickup_confirmation'
                valuePropName='checked'
                noStyle
              >
                <Checkbox className='notification-checkbox'>
                  <span className='notification-checkbox-label'>
                    In transit (picked up)
                  </span>
                </Checkbox>
              </Form.Item>
              {isWorldCourierCompany && (
                <Form.Item name='is_incomplete' valuePropName='checked' noStyle>
                  <Checkbox className='notification-checkbox'>
                    <span className='notification-checkbox-label'>
                      Incomplete
                    </span>
                  </Checkbox>
                </Form.Item>
              )}
              {!isWorldCourierCompany && (
                <Form.Item
                  name='is_delivery_exception'
                  valuePropName='checked'
                  noStyle
                >
                  <Checkbox className='notification-checkbox'>
                    <span className='notification-checkbox-label'>
                      Delivery exception
                    </span>
                  </Checkbox>
                </Form.Item>
              )}
              <Form.Item
                name='is_delivery_confirmation'
                valuePropName='checked'
                noStyle
              >
                <Checkbox className='notification-checkbox'>
                  <span className='notification-checkbox-label'>Delivered</span>
                </Checkbox>
              </Form.Item>
            </Flex>
          </Form.Item>
        </div>
      );

    case 'AT_LOCATION':
      return (
        <Flex vertical gap={4}>
          <Flex vertical gap={4}>
            <CustomShipmentText
              text={'Shipment arriving at location'}
              fontSize='14px'
              fontWeight={600}
              marginBottom={'4px'}
            />
            <Form.Item
              className='custom-input form-input-style'
              name='location'
              rules={[
                {
                  required: true,
                  message: 'Please select a shipment arrival location',
                },
              ]}
            >
              <CustomSelect
                labelInValue
                width='100%'
                placeholder='Select...'
                suffixIcon={
                  <DownOutlined style={{ color: 'var(--text-secondary)' }} />
                }
                options={locationsList?.map((loc) => ({
                  value: loc?.id,
                  label: loc?.name,
                }))}
                bgColor='var(--light-gray)'
                textColor='var(--text-secondary)'
                borderRadius='8px'
                height='36px'
                border='none'
                placeholderColor='var(--text-secondary)'
                filterOption={false}
                onPopupScroll={handlePopupScroll}
                notFoundContent={
                  dropdownLoading ? (
                    <div
                      style={{
                        padding: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <span></span>
                    </div>
                  ) : (
                    <div style={{ padding: 8 }}>No locations found</div>
                  )
                }
                dropdownRender={(menu: React.ReactNode) => (
                  <>
                    <div
                      style={{
                        padding: '6px 16px 12px',
                        borderBottom: '1px solid #eee',
                      }}
                    >
                      <SearchInput
                        placeholder='Search location'
                        width='100%'
                        onChange={(val) => setSearch(val)}
                        onClear={() => setSearch('')}
                      />
                    </div>
                    <div
                      ref={dropdownScrollElRef}
                      style={{ maxHeight: 256, overflow: 'auto' }}
                      onScroll={(e) => handlePopupScroll(e)}
                    >
                      {menu}
                    </div>
                    {dropdownLoading && (
                      <div
                        style={{
                          padding: 8,
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <Spin size='small' />
                      </div>
                    )}
                  </>
                )}
              />
            </Form.Item>
          </Flex>

          <p
            className='font-12 fw-400 lh-20'
            style={{ color: 'var(--text-secondary)' }}
          >
            Not in the list? Create one{' '}
            <Link
              className='black-link'
              to='/settings/location'
              state={{ openAddModal: true }}
            >
              here
            </Link>
          </p>
        </Flex>
      );

    case 'LOW_INVENTORY':
      return (
        <Flex vertical gap={4}>
          <Flex vertical gap={16}>
            <Flex vertical gap={4}>
              <CustomShipmentText
                text={'Which item does this apply to'}
                fontSize='14px'
                fontWeight={600}
                marginBottom={'4px'}
              />
              <Form.Item
                className='custom-input form-input-style'
                name='item'
                normalize={(value) => {
                  if (Array.isArray(value)) {
                    return value.map((v) =>
                      typeof v === 'object' ? v.value || v.id : v
                    );
                  }
                  return value ? [value] : [];
                }}
                rules={[
                  {
                    required: true,
                    message: 'Please select an item',
                  },
                ]}
              >
                <CustomSelect
                  width='100%'
                  placeholder='Select...'
                  suffixIcon={<DownOutlined />}
                  disabled={isInventory && !selecteInventory?.id}
                  style={{ minHeight: '36px' }}
                  options={
                    isInventory
                      ? result
                      : subItemsList?.map((item) => ({
                          value: item?.id,
                          label: `${item?.sku || item?.name}`,
                          disabled: item?.enabled === false,
                          tooltipReason: !item?.enabled
                            ? 'Reorder target is missing'
                            : item?.sku || item?.name,
                        }))
                  }
                  bgColor='var(--light-gray)'
                  textColor='var(--text-secondary)'
                  borderRadius='8px'
                  height='auto'
                  border='none'
                  placeholderColor='var(--text-secondary)'
                  filterOption={false}
                  onChange={
                    isInventory
                      ? (value: string) => {
                          form.setFieldValue('item', value ? [value] : []);
                        }
                      : handleItemChange
                  }
                  onPopupScroll={
                    isInventory ? undefined : handleSubItemPopupScroll
                  }
                  notFoundContent={
                    <div className='p-2'>No inventory found</div>
                  }
                  dropdownRender={(menu: React.ReactNode) => (
                    <>
                      {!isInventory && (
                        <div className='form-dropdown-input'>
                          <SearchInput
                            placeholder={'Search inventory'}
                            width='100%'
                            onChange={(val) => setSubItemSearch(val)}
                            onClear={() => setSubItemSearch('')}
                          />
                        </div>
                      )}
                      <div
                        ref={subItemDropdownScrollElRef}
                        className='select-dropdown-h'
                        style={{ maxHeight: 256, overflow: 'auto' }}
                      >
                        {menu}
                      </div>
                      {subItemdropdownLoading && (
                        <Flex className='p-2' justify='center'>
                          <Spin size='small' />
                        </Flex>
                      )}
                    </>
                  )}
                />
              </Form.Item>
            </Flex>
            <Flex vertical gap={4}>
              <CustomShipmentText
                text={'Which locations does this apply to'}
                fontSize='14px'
                fontWeight={600}
                marginBottom={'4px'}
              />
              <Form.Item
                className='custom-input form-input-style'
                name='locations'
                normalize={(value) => {
                  if (Array.isArray(value)) {
                    return value.map((v) =>
                      typeof v === 'object' ? v.value || v.id : v
                    );
                  }
                  return value || [];
                }}
                rules={[
                  {
                    required: true,
                    message: 'Please select location',
                  },
                ]}
              >
                <CustomSelect
                  mode='multiple'
                  width='100%'
                  className='gray-tag-select'
                  placeholder='Select...'
                  style={{ minHeight: '36px' }}
                  suffixIcon={
                    <DownOutlined style={{ color: 'var(--text-secondary)' }} />
                  }
                  options={inventoryLocationsList
                    ?.filter((loc) =>
                      loc?.name
                        ?.toLowerCase()
                        .includes(inventorySearch.toLowerCase())
                    )
                    ?.map((loc) => ({
                      value: loc?.id,
                      label: loc?.name,
                    }))}
                  bgColor='var(--light-gray)'
                  textColor='var(--text-secondary)'
                  borderRadius='8px'
                  height='auto'
                  border='none'
                  placeholderColor='var(--text-secondary)'
                  filterOption={false}
                  onPopupScroll={isInventory ? undefined : handlePopupScroll}
                  onChange={(values: string[]) => {
                    // form field stores string array for payload
                    form.setFieldValue('locations', values || []);
                  }}
                  notFoundContent={
                    isInventory ? (
                      <div style={{ padding: 8 }}>
                        No inventory locations found
                      </div>
                    ) : dropdownLoading ? (
                      <div
                        style={{
                          padding: 8,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <span></span>
                      </div>
                    ) : (
                      <div style={{ padding: 8 }}>No locations found</div>
                    )
                  }
                  dropdownRender={(menu: React.ReactNode) => (
                    <>
                      <div
                        style={{
                          padding: '6px 16px 12px',
                          borderBottom: '1px solid #eee',
                        }}
                      >
                        <SearchInput
                          placeholder={'Search location'}
                          width='100%'
                          onChange={(val) => setInventorySearch(val)}
                          onClear={() => setInventorySearch('')}
                        />
                      </div>
                      <div
                        ref={isInventory ? undefined : dropdownScrollElRef}
                        style={{ maxHeight: 256, overflow: 'auto' }}
                        onScroll={
                          isInventory ? undefined : (e) => handlePopupScroll(e)
                        }
                      >
                        {menu}
                      </div>
                      {!isInventory && dropdownLoading && (
                        <div
                          style={{
                            padding: 8,
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          <Spin size='small' />
                        </div>
                      )}
                    </>
                  )}
                />
              </Form.Item>
            </Flex>
          </Flex>
        </Flex>
      );

    case 'ETA_BASED':
      return (
        <Col span={24} style={{ padding: 0 }}>
          <CustomShipmentText
            text={'ETA changes by more than'}
            fontSize='14px'
            fontWeight={600}
            marginBottom={'4px'}
          />
          <Form.Item
            name='change_by'
            rules={[{ required: true, message: 'Please select an ETA change' }]}
            style={{ marginBottom: '10px' }}
          >
            <CustomSelect
              width='100%'
              suffixIcon={
                <DownOutlined style={{ color: 'var(--text-secondary)' }} />
              }
              placeholder='Select...'
              options={ETAChangedByList}
              bgColor='var(--light-gray)'
              textColor='var(--text-secondary)'
              borderRadius='8px'
              height='36px'
              border='none'
              placeholderColor='var(--text-secondary)'
            />
          </Form.Item>
        </Col>
      );
    case 'AI_ACTION':
      return (
        <Col span={24} style={{ padding: 0 }}>
          <CustomShipmentText
            text={'Action type'}
            fontSize='14px'
            fontWeight={600}
            marginBottom={'4px'}
          />
          <Form.Item
            name='ai_action_type'
            rules={[
              { required: true, message: 'Please select an action type' },
            ]}
            style={{ marginBottom: '10px' }}
          >
            <CustomSelect
              width='100%'
              suffixIcon={
                <DownOutlined style={{ color: 'var(--text-secondary)' }} />
              }
              placeholder='Select...'
              options={actionTypeList}
              bgColor='var(--light-gray)'
              textColor='var(--text-secondary)'
              borderRadius='8px'
              height='36px'
              border='none'
              placeholderColor='var(--text-secondary)'
            />
          </Form.Item>
        </Col>
      );

    case 'TEMPERATURE_GXP':
      return (
        <Flex vertical gap={4}>
          <Flex vertical gap={4}>
            <CustomShipmentText
              text={'Report event'}
              fontSize='14px'
              fontWeight={600}
              marginBottom={'4px'}
            />
            <Form.Item
              className='custom-input form-input-style'
              name='gxp_report'
              rules={[
                {
                  required: true,
                  message: 'Please select a report event',
                },
              ]}
            >
              <CustomSelect
                className='gxp-report-event gray-tag-select'
                mode='multiple'
                width='100%'
                placeholder='Select...'
                suffixIcon={
                  <DownOutlined style={{ color: 'var(--text-secondary)' }} />
                }
                options={GxPReportEventOptions}
                bgColor='var(--light-gray)'
                textColor='var(--text-secondary)'
                borderRadius='8px'
                height='auto'
                border='none'
                placeholderColor='var(--text-secondary)'
                filterOption={false}
                showSearch={false}
                menuItemSelectedIcon={null}
                optionRender={(option) => (
                  <Flex align='center' gap={8}>
                    <Checkbox
                      checked={gxpReportEvents.includes(option.value as string)}
                    />
                    <span>{option.label}</span>
                  </Flex>
                )}
              />
            </Form.Item>
          </Flex>
        </Flex>
      );

    default:
      return null;
  }
};

export default AlertFieldByType;
